import type * as React from 'react';

import type * as api from '@gitbook/api-client';
import { BillingTrialStatus } from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import { canPlanMigrateToNewPricing } from '@gitbook/app-pricing';
import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    Description,
    Heading,
    SidebarAlert,
    SidebarAlertActions,
    toast,
} from '@gitbook/spine-react';

import { getTrialEndingNotice } from '../../Components';
import { useNavigationState } from '../../navigation';
import {
    apiClient,
    calculateDaysDifference,
    getUpgradeScreenNavigationTarget,
    useTrial,
} from '../../state';

/**
 * Display an alert in the sidebar body for an organization.
 * It doesn't suspend rendering while fetching the billing to prevent making
 * the whole sidebar lag uselessly.
 */
export function OrganizationAlert(props: {
    organization: api.Organization;
    className?: string;
}): React.ReactElement | null {
    const { organization, className } = props;
    const canAdminBilling = organization.permissions.admin;
    const status = organization.trial.status;

    // First: show the alert to upgrade if the organization's trial has ended

    /*
     * Explicitly check if trialEnd is set, as BillingTrialStatus can be 'expired' even without a trialEnd.
     * See the special case for orgs created < 2022-09-06 in getOrganizationTrialStatus.
     */
    const hasTrialEndDate = Boolean(organization.trial.endDate);
    if (status === BillingTrialStatus.Active && hasTrialEndDate) {
        return (
            <BillingTrialAlert
                organization={organization}
                canAdminBilling={canAdminBilling}
                className={className}
            />
        );
    }

    // Second: show an alert to fix payment if billing hasPaymentFailed is set
    if (
        status !== BillingTrialStatus.Active &&
        status !== BillingTrialStatus.Expired &&
        organization.billing?.hasPaymentFailed &&
        canAdminBilling
    ) {
        return <BillingFailedPaymentAlert organization={organization} className={className} />;
    }

    // Third: show an alert prompting organizations to migrate to new pricing
    if (canAdminBilling && canPlanMigrateToNewPricing(organization.plan)) {
        return <BillingSelfServeUpgradeAlert organization={organization} className={className} />;
    }

    return null;
}

/**
 * Alert when the billing hasPaymentFailed is set
 */
function BillingFailedPaymentAlert({
    organization,
    className,
}: {
    organization: api.Organization;
    className?: string;
}): React.ReactNode {
    if (!organization.billing?.hasPaymentFailed) {
        return null;
    }

    const reason = 'sidebar_cta_payment_failure';

    return (
        <SidebarAlert className={className}>
            <Heading>Payment failed</Heading>
            <Description>
                We couldn’t process your payment. Check your payment method to keep your
                organization active.
            </Description>
            <SidebarAlertActions>
                <Button
                    label="Check payment method"
                    kind="danger"
                    size="small"
                    className="grow"
                    trailingIcon={Icon.ArrowUpRight}
                    loadable
                    onPress={async () => {
                        analytics.logEvent(
                            analytics.createOrgEvent(organization.id, {
                                type: 'organization_open_billing',
                                reason,
                            })
                        );

                        await toast.catch(async () => {
                            const { data } = await apiClient.billing.getOrganizationBillingPortal(
                                organization.id
                            );

                            window.location.href = data.url;
                        }, 'Error while accessing billing portal');
                    }}
                />
            </SidebarAlertActions>
        </SidebarAlert>
    );
}

/**
 * Alert when the trial has finished or is about to finish.
 */
function BillingTrialAlert({
    organization,
    canAdminBilling,
    className,
}: {
    organization: api.Organization;
    canAdminBilling: boolean;
    className?: string;
}) {
    const { screen: currentScreen } = useNavigationState();

    const trial = useTrial({ organization });
    const reason = 'sidebar_cta';

    return (
        <SidebarAlert className={className} data-testid="sidebar.trialAlert">
            <Heading>
                {getTrialEndingNotice({
                    daysLeft: trial.daysLeft,
                })}
            </Heading>
            <Description>
                {canAdminBilling
                    ? trial.status === BillingTrialStatus.Active
                        ? 'Upgrade at any time to continue accessing paid features.'
                        : 'Subscribe today to keep all the features you’ve tried.'
                    : 'Ask your admin to subscribe to keep all the features you’ve tried.'}
            </Description>
            <SidebarAlertActions>
                {canAdminBilling ? (
                    <Button
                        label="Upgrade"
                        kind="upgrade"
                        size="small"
                        className="grow"
                        href={getUpgradeScreenNavigationTarget({
                            organizationId: organization.id,
                            reason,
                            returnScreen: currentScreen,
                        })}
                        onPress={() => {
                            analytics.logEvent(
                                analytics.createOrgEvent(organization.id, {
                                    type: 'organization_open_upgrade',
                                    currentFeaturesSet: organization.plan,
                                    reason,
                                })
                            );
                        }}
                    />
                ) : null}
            </SidebarAlertActions>
        </SidebarAlert>
    );
}

/**
 * Alert when the the organization can upgrade to new pricing
 */
function BillingSelfServeUpgradeAlert({
    organization,
    className,
}: {
    organization: api.Organization;
    className?: string;
}) {
    if (!organization.internal_billingMigration) {
        return;
    }

    const { deadline, discountPercent } = organization.internal_billingMigration;

    if (!deadline || !discountPercent) {
        return;
    }

    const daysLeft = calculateDaysDifference(new Date(), new Date(deadline));

    return (
        <SidebarAlert className={className}>
            <Heading>Your pricing plan is changing</Heading>
            <Description>
                Your current plan will be automatically migrated{' '}
                {daysLeft > 0 ? (
                    <>
                        in <strong>{daysLeft}</strong> {daysLeft === 1 ? 'day' : 'days'}
                    </>
                ) : (
                    'soon'
                )}
                . Switch now to save <strong>{discountPercent}% for up to 12 months</strong>!
            </Description>
            <SidebarAlertActions>
                <Button
                    label="Switch now and save"
                    kind="upgrade"
                    size="small"
                    href={{
                        overlay: {
                            type: 'org_settings',
                            organization: organization.id,
                            tab: { type: 'upgrade' },
                            hash: 'sidebar_cta',
                        },
                    }}
                />
            </SidebarAlertActions>
        </SidebarAlert>
    );
}
